import { type HighnoteTransactionEvent, HighnoteTransactionStatus } from 'API';
// SEE-1232 row j: read from CDK AppSync. The superseded Amplify document stays in
// graphql_custom/transactions (SEE-1238 removes it).
import { transactionService } from 'app/services/transaction.service';
import { useEffect, useState } from 'react';

/**
 * For cleared transactions, fetches the original authorization event's date
 * by querying all events for the same transactionId (sorted ASC) and returning
 * the earliest event's eventDate.
 *
 * For pending transactions, the current eventDate is the authorization date.
 */
export function useAuthorizationDate(transaction?: HighnoteTransactionEvent): {
  authorizationDate: string | null;
  isLoading: boolean;
} {
  const [authorizationDate, setAuthorizationDate] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isPending = transaction?.status === HighnoteTransactionStatus.APPROVED;
  const transactionId = transaction?.transactionId;
  const eventDate = transaction?.eventDate;

  useEffect(() => {
    // Reset first so early-returns and cancelled fetches can't leave isLoading stuck at true
    setIsLoading(false);

    if (isPending) {
      setAuthorizationDate(eventDate ?? null);
      return;
    }

    if (!transactionId) {
      setAuthorizationDate(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        // Transport flip only — the CDK read is a real GSI page ordered by eventDate, so
        // ASC + limit 1 still returns the earliest event.
        const page = await transactionService.listHighnoteTransactionEventsByTransactionId({
          transactionId,
          sortDirection: 'ASC',
          limit: 1,
        });

        if (cancelled) return;

        const firstEvent = page.items[0];
        setAuthorizationDate(firstEvent?.eventDate ?? eventDate ?? null);
      } catch {
        if (!cancelled) {
          setAuthorizationDate(eventDate ?? null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // `transaction` intentionally excluded — depending on primitives avoids refetching on reference-only changes
  }, [isPending, transactionId, eventDate]);

  return { authorizationDate, isLoading };
}
